/** État d'une section : vide, en cours, renseignée, ou complétée.

Complété : chaque élément prévu a une réponse.
- Zone par anomalie (écoles, 2ème, réception) : chaque anomalie est RAS ou a au moins une salle.
  Un ancien rapport saisi salle par salle reste complet si chaque salle a une réponse.
- Local unique (bureau direction, fenêtres, sous-sols…) : RAS, une anomalie, un texte hérité ou une remarque.
- Heure : une heure valide HH:MM. L'heure du contrôle de la zone en fait partie.
- Température : un nombre valide et une heure valide, pour chaque frigo affiché.
- Les précisions facultatives (texte libre de remise, passages en plus) ne comptent pas.

Vide et Renseigné gardent leur sens : rien, ou une partie seulement.
Les listes par défaut sont celles du formulaire. Le menu passe la config réelle.
*/

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const TEMP_RE = /^[+\-−–]?\d+(?:[.,]\d+)?$/;

const text = (value) => String(value ?? '').trim().length > 0;

export function validTime(value) {
  return TIME_RE.test(String(value ?? '').trim());
}

export function validTemperature(value) {
  const raw = String(value ?? '').trim().replace(/°\s*c/i, '').trim();
  return TEMP_RE.test(raw);
}

const CHECKLIST_OPTIONS = ['RAS', 'Portes non verrouillées', 'Lumières allumées', 'Projecteurs allumés', 'PCs allumés', 'Poubelles non vidées', 'Fenêtres ouvertes', 'Stores ouverts'];

export const DEFAULT_SECTION_CATALOG = {
  ecoles: {
    rooms: ['001', '003', '004', '005', '006', '007', '008', '009', '010', '011', '013'],
    options: CHECKLIST_OPTIONS,
    door: true,
  },
  etage2: {
    rooms: ['202a', '202b', '203', '204'],
    options: CHECKLIST_OPTIONS,
  },
  reception: {
    rooms: ['001', '005', '016'],
    options: ['RAS', 'Portes non verrouillées', 'Lumières allumées'],
    extraItems: ['Bureau direction', 'Fenêtres coulissantes'],
  },
  restaurant: { items: ['Fenêtres coulissantes restaurant'] },
  cuisine: { items: ['Porte de secours et livraison côté parking'] },
  sousSols: {
    items: [
      'Porte couloir accès SAS >> WC',
      'Cave',
      'Chambres froides',
      'Local Economat',
      'Local Buanderie',
      'Local Pâtisserie',
      'Garage porte vitrée',
      'Garage porte automatiques',
      'Salle Carnotzet',
      'Salle S03',
      'Dépôt sous-sol',
      'Contrôle du café Vatel (fermeture 23:00h)',
      'Fermeture salle Étudiants (student life)',
    ],
  },
  floors: [
    'Sous-sol',
    'Rez-de-chaussée',
    '1er',
    'Buanderie étudiants',
    'Administration 120',
    '2ème',
    'Vestiaires f/h',
    '3ème',
    '4ème',
    '5ème',
  ],
  materiel: ['iPhone en charge', 'Pass général', 'Lampe de poche', 'Tiroir fermé à clé et clé retirée', 'Autre'],
};

function anyMarked(value) {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (Array.isArray(value)) return value.some((item) => anyMarked(item));
  if (typeof value === 'object') return Object.values(value).some((item) => anyMarked(item));
  return text(value);
}

function anomalyCategories(options) {
  return (options || []).filter((option) => option && option !== 'RAS');
}

function allCategoriesRas(ras, options) {
  const categories = anomalyCategories(options);
  return categories.length > 0 && categories.every((category) => ras?.[category] === true);
}

function itemAnswered(value, remark) {
  return text(remark) || text(value);
}

function rollup(flags, started) {
  if (flags.length > 0 && flags.every(Boolean)) return 'completed';
  if (started || flags.some(Boolean)) return 'filled';
  return 'empty';
}

function splitEntries(value) {
  return String(value ?? '').split(', ').map((part) => part.trim()).filter(Boolean);
}

function entryCategory(entry) {
  const match = String(entry || '').match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  return match ? match[1].trim() : String(entry || '').trim();
}

function roomHasCategory(value, category) {
  return splitEntries(value).some((entry) => entryCategory(entry) === category);
}

/* Zone saisie comme sur le papier : une ligne par anomalie.
Une anomalie est répondue si elle est RAS, ou si au moins une salle l'a.
Les anciens rapports, saisis salle par salle, restent complets si chaque salle a une réponse. */
function zoneChecklist(form, key, spec, { door = false, extraItems = [] } = {}) {
  const rooms = spec?.rooms || [];
  const options = spec?.options || [];
  const ras = form[`${key}_ras`] || {};
  const controle = form[`${key}_controle`] || {};
  const remarks = form[`${key}_remarks`] || {};
  const categories = anomalyCategories(options);
  const categoryAnswered = (category) => ras?.[category] === true
    || rooms.some((room) => roomHasCategory(controle[room], category));
  const byCategory = categories.length > 0 && categories.every(categoryAnswered);
  const byRoom = rooms.length > 0 && rooms.every((room) => itemAnswered(controle[room], remarks[room]));
  const flags = [byCategory || byRoom || allCategoriesRas(ras, options)];
  for (const item of extraItems) flags.push(itemAnswered(controle[item], remarks[item]));
  if (door) flags.push(text(form.ecole_porte_secours));
  flags.push(validTime(form.zone_times?.[key]));
  const started = rooms.some((room) => itemAnswered(controle[room], remarks[room]))
    || extraItems.some((item) => itemAnswered(controle[item], remarks[item]))
    || anyMarked(ras)
    || text(form.zone_times?.[key])
    || (door && text(form.ecole_porte_secours));
  return rollup(flags, started);
}

function itemZone(form, key, items) {
  const controle = form[`${key}_controle`] || {};
  const remarks = form[`${key}_remarks`] || {};
  const flags = (items || []).map((item) => itemAnswered(controle[item], remarks[item]));
  flags.push(validTime(form.zone_times?.[key]));
  const started = flags.some(Boolean) || anyMarked(controle) || anyMarked(remarks);
  return rollup(flags, started);
}

function passageSlots(values, fallbackCount) {
  const slots = Array.isArray(values) && values.length ? values : Array(fallbackCount).fill('');
  const flags = slots.map((value) => validTime(value));
  const started = slots.some((value) => text(value));
  return rollup(flags, started);
}

export function sectionStatus(id, data = {}, extras = {}) {
  const form = data || {};
  const catalog = { ...DEFAULT_SECTION_CATALOG, ...(extras.catalog || {}) };
  if (id === 'general') {
    const hours = [form.shift_start, form.shift_end].map(validTime);
    const reception = [form.reception_name, form.reception_contact, form.reception_departure].some(text);
    if (hours.every(Boolean)) return 'completed';
    if (hours.some(Boolean) || reception || [form.shift_start, form.shift_end].some(text)) return 'partial';
    return 'empty';
  }
  if (id === 'ecoles') return zoneChecklist(form, 'ecoles', catalog.ecoles, { door: true });
  if (id === 'etage2') return zoneChecklist(form, 'etage2', catalog.etage2);
  if (id === 'reception') {
    return zoneChecklist(form, 'reception', catalog.reception, {
      extraItems: catalog.reception?.extraItems || [],
    });
  }
  if (id === 'restaurant') return itemZone(form, 'restaurant', catalog.restaurant?.items);
  if (id === 'cuisine') return itemZone(form, 'cuisine', catalog.cuisine?.items);
  if (id === 'sous-sols') return itemZone(form, 'sous_sols', catalog.sousSols?.items);
  if (id === 'temperatures') {
    const rows = Array.isArray(form.temperature_checks) ? form.temperature_checks : [];
    const flags = rows.map((row) => validTemperature(row?.temperature) && validTime(row?.time));
    const started = rows.some((row) => text(row?.temperature) || text(row?.time));
    return rollup(flags, started);
  }
  if (id === 'manoeuvres') {
    const names = Array.isArray(catalog.manoeuvres)
      ? catalog.manoeuvres
      : [...new Set([
        ...Object.keys(form.manoeuvres || {}),
        ...Object.keys(form.manoeuvre_times || {}),
      ])];
    const flags = names.map((name) => form.manoeuvres?.[name] === true || validTime(form.manoeuvre_times?.[name]));
    return rollup(flags, flags.some(Boolean));
  }
  if (id === 'controle-exterieurs') return passageSlots(form.controle_exterieurs, 6);
  if (id === 'controle-internes') return passageSlots(form.controle_internes_hotel, 6);
  if (id === 'observations-etages') {
    const floors = catalog.floors || [];
    const flags = floors.map((floor) => text(form.observations_etages?.[floor]) || text(form.observations_remarks?.[floor]));
    return rollup(flags, flags.some(Boolean));
  }
  if (id === 'incidents') {
    const rows = (form.incidents || []).filter((row) => text(row?.time) || text(row?.description));
    const photos = Number(extras.photoCount) > 0;
    if (!rows.length) return photos ? 'filled' : 'empty';
    const flags = rows.map((row) => validTime(row?.time) && text(row?.description));
    return rollup(flags, true);
  }
  if (id === 'remise-materiel') {
    const flags = [
      text(form.remise_materiel_personnel),
      validTime(form.remise_materiel_heure),
      validTime(form.remise_materiel_heure_agent),
    ];
    const started = flags.some(Boolean) || text(form.remise_materiel_matin);
    return rollup(flags, started);
  }
  if (id === 'materiel') {
    const keys = Object.keys(form.material_checklist || {});
    const names = keys.length ? keys : (catalog.materiel || []);
    const flags = names.map((name) => form.material_checklist?.[name] === true);
    return rollup(flags, flags.some(Boolean));
  }
  return 'empty';
}

export const SECTION_STATUS_LABEL = {
  empty: 'Vide',
  partial: 'En cours',
  filled: 'Renseigné',
  completed: 'Complété',
};
